import { useEffect, useRef, useState, type ChangeEvent, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { AlertCircle, Camera, CheckCircle2, Loader2, ShieldCheck, Upload, UserRound, Wand2 } from 'lucide-react'
import {
  useExtractPublicGuestIntakePhoto,
  usePublicGuestIntakeSession,
  useSubmitPublicGuestIntake,
  useUploadPublicGuestIntakePhoto,
} from '../api/hooks'
import type {
  HotelProfile,
  MigrationSex,
  PublicCheckInPrimary,
  PublicGuestIntakePhoto,
  PublicGuestIntakeSubmitPayload,
  ReservationDocumentType,
} from '../api/types'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { PhoneInput } from '@/components/PhoneInput'

const documentLabels: Record<ReservationDocumentType, string> = {
  dni: 'DNI / Identidad',
  passport: 'Pasaporte',
  license: 'Licencia',
  other: 'Otro',
}

const sexLabels: Record<Exclude<MigrationSex, ''>, string> = {
  M: 'Masculino',
  F: 'Femenino',
}

const defaultMigrationDestination = 'Comayagua'

const blankForm: PublicGuestIntakeSubmitPayload = {
  first_name: '',
  last_name: '',
  document_type: 'dni',
  document_number: '',
  date_of_birth: '',
  sex: '',
  document_issue_country: '',
  document_expiration_date: '',
  migration_nationality: '',
  migration_origin: '',
  migration_destination: defaultMigrationDestination,
  email: '',
  phone: '',
  accepted_terms: false,
}

export function PublicGuestIntakePage({ hotel }: { hotel?: HotelProfile }) {
  const { token } = useParams<{ token: string }>()
  const sessionQuery = usePublicGuestIntakeSession(token)
  const uploadPhoto = useUploadPublicGuestIntakePhoto(token)
  const extractPhoto = useExtractPublicGuestIntakePhoto(token)
  const submitGuest = useSubmitPublicGuestIntake(token)
  const [form, setForm] = useState<PublicGuestIntakeSubmitPayload>(blankForm)
  const [initialized, setInitialized] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const session = sessionQuery.data
  const hasDocumentPhoto = Boolean(session?.photos.length)

  useEffect(() => {
    if (!session || initialized) return
    setForm((prev) => ({
      ...prev,
      phone: session.default_phone || '',
    }))
    setInitialized(true)
  }, [initialized, session])

  const canSubmit = Boolean(
    form.first_name.trim() &&
    form.last_name.trim() &&
    form.document_number.trim() &&
    form.phone.trim() &&
    hasDocumentPhoto &&
    form.accepted_terms &&
    !submitGuest.isPending,
  )

  const updateField = <K extends keyof PublicGuestIntakeSubmitPayload>(
    field: K,
    value: PublicGuestIntakeSubmitPayload[K],
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  const applySuggestions = (suggestions: Partial<PublicCheckInPrimary>) => {
    setForm((prev) => ({
      ...prev,
      first_name: suggestions.first_name || prev.first_name,
      last_name: suggestions.last_name || prev.last_name,
      document_type: suggestions.document_type || prev.document_type,
      document_number: suggestions.document_number || prev.document_number,
      date_of_birth: suggestions.date_of_birth || prev.date_of_birth || '',
      sex: suggestions.sex || prev.sex || '',
      document_issue_country: (
        suggestions.document_issue_country || prev.document_issue_country || ''
      ),
      document_expiration_date: (
        suggestions.document_expiration_date || prev.document_expiration_date || ''
      ),
      migration_nationality: (
        suggestions.migration_nationality || prev.migration_nationality || ''
      ),
      migration_origin: suggestions.migration_origin || prev.migration_origin || '',
      migration_destination: (
        suggestions.migration_destination ||
        prev.migration_destination ||
        defaultMigrationDestination
      ),
    }))
  }

  const extractAndApply = async (photo: PublicGuestIntakePhoto) => {
    const result = await extractPhoto.mutateAsync({ photoId: photo.id })
    applySuggestions(result.suggestions || {})
  }

  const handleUpload = async (file: File) => {
    setSubmitError('')
    try {
      const uploadedPhoto = await uploadPhoto.mutateAsync({ file })
      await sessionQuery.refetch()
      try {
        await extractAndApply(uploadedPhoto)
      } catch {
        setSubmitError('La foto se subió, pero no se pudieron leer los datos del documento.')
      }
      await sessionQuery.refetch()
    } catch {
      setSubmitError('No se pudo subir la imagen. Intenta de nuevo.')
    }
  }

  const handleExtract = async (photo: PublicGuestIntakePhoto) => {
    setSubmitError('')
    try {
      await extractAndApply(photo)
      await sessionQuery.refetch()
    } catch {
      setSubmitError('No se pudieron leer los datos del documento.')
    }
  }

  const handleSubmit = async () => {
    if (!canSubmit) return
    setSubmitError('')
    try {
      await submitGuest.mutateAsync({
        ...form,
        date_of_birth: form.date_of_birth || null,
        document_expiration_date: form.document_expiration_date || null,
        migration_destination: form.migration_destination || defaultMigrationDestination,
      })
      await sessionQuery.refetch()
    } catch {
      setSubmitError('No se pudo completar el registro. Revisa los datos.')
    }
  }

  if (sessionQuery.isLoading) {
    return (
      <StandaloneShell hotel={hotel}>
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-terracotta" />
        </div>
      </StandaloneShell>
    )
  }

  if (sessionQuery.isError || !session) {
    return (
      <StandaloneShell hotel={hotel}>
        <Card>
          <CardContent className="flex items-start gap-3 p-5 sm:p-6">
            <AlertCircle className="mt-1 h-5 w-5 shrink-0 text-red-600" />
            <div>
              <h1 className="font-heading text-xl font-bold text-wood-dark sm:text-2xl">
                Enlace no disponible
              </h1>
              <p className="mt-2 text-sm text-stone">
                El enlace de registro no existe o ya expiró.
              </p>
            </div>
          </CardContent>
        </Card>
      </StandaloneShell>
    )
  }

  const completed = session.already_submitted || Boolean(submitGuest.data)

  return (
    <StandaloneShell hotel={hotel}>
      <div className="space-y-4 sm:space-y-5">
        <div>
          <h1 className="font-heading text-2xl font-bold text-wood-dark sm:text-3xl">
            Registro de huésped
          </h1>
          <p className="mt-1 text-sm text-stone">
            {session.room
              ? `Habitación ${session.room.room_type} #${session.room.number}`
              : 'Datos para recepción'}
          </p>
        </div>

        {completed ? (
          <Card>
            <CardContent className="flex items-start gap-3 p-6">
              <CheckCircle2 className="mt-1 h-6 w-6 shrink-0 text-green-700" />
              <div>
                <h2 className="font-heading text-2xl font-bold text-wood-dark">
                  Datos recibidos
                </h2>
                <p className="mt-2 text-sm text-stone">
                  Recepción ya puede continuar con la reservación.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl">
                  <UserRound className="h-5 w-5" />
                  Documento
                </CardTitle>
                <CardDescription>Foto del documento y datos de contacto.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <PhotoPanel
                  photos={session.photos}
                  busy={uploadPhoto.isPending || extractPhoto.isPending}
                  onUpload={handleUpload}
                  onExtract={handleExtract}
                />
                <GuestFields form={form} onChange={updateField} />
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-4 p-6">
                <label className="flex items-start gap-3 text-sm text-wood-dark">
                  <input
                    type="checkbox"
                    checked={form.accepted_terms}
                    onChange={(event) => updateField('accepted_terms', event.target.checked)}
                    className="mt-1 h-4 w-4"
                  />
                  Confirmo que la información enviada corresponde al huésped.
                </label>
                {submitError ? (
                  <p className="rounded-md bg-red-50 p-3 text-sm text-red-700">
                    {submitError}
                  </p>
                ) : null}
                {!hasDocumentPhoto ? (
                  <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-800">
                    Subí una foto del documento para enviar el registro.
                  </p>
                ) : null}
                <Button size="lg" className="w-full" disabled={!canSubmit} onClick={handleSubmit}>
                  {submitGuest.isPending ? (
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  ) : (
                    <CheckCircle2 className="mr-2 h-5 w-5" />
                  )}
                  Enviar datos
                </Button>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </StandaloneShell>
  )
}

function StandaloneShell({
  hotel,
  children,
}: {
  hotel?: HotelProfile
  children: ReactNode
}) {
  const hotelName = hotel?.hotel_name || 'Hotel Nueva Valladolid'
  const logo = hotel?.logo_image
  return (
    <div className="min-h-screen bg-cream-light">
      <header className="sticky top-0 z-10 border-b border-cream-dark bg-white/95 backdrop-blur">
        <div className="mx-auto flex w-full max-w-3xl items-center gap-3 px-4 py-3">
          {logo ? (
            <img src={logo} alt={hotelName} className="h-8 w-8 rounded object-contain" />
          ) : null}
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-wood-dark">{hotelName}</p>
            <p className="truncate text-xs text-stone">Registro seguro</p>
          </div>
          <ShieldCheck className="ml-auto h-5 w-5 text-terracotta" aria-hidden="true" />
        </div>
      </header>
      <main className="mx-auto w-full max-w-3xl px-4 pb-16 pt-5 sm:pt-6">{children}</main>
    </div>
  )
}

function GuestFields({
  form,
  onChange,
}: {
  form: PublicGuestIntakeSubmitPayload
  onChange: <K extends keyof PublicGuestIntakeSubmitPayload>(
    field: K,
    value: PublicGuestIntakeSubmitPayload[K],
  ) => void
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <TextField
        label="Nombre"
        value={form.first_name}
        onChange={(next) => onChange('first_name', next)}
        autoComplete="given-name"
        autoCapitalize="words"
      />
      <TextField
        label="Apellido"
        value={form.last_name}
        onChange={(next) => onChange('last_name', next)}
        autoComplete="family-name"
        autoCapitalize="words"
      />
      <DocumentTypeField
        value={form.document_type}
        onChange={(next) => onChange('document_type', next)}
      />
      <TextField
        label="Número de documento"
        value={form.document_number}
        onChange={(next) => onChange('document_number', next)}
        autoCapitalize="characters"
      />
      <TextField
        label="Fecha de nacimiento"
        type="date"
        value={form.date_of_birth || ''}
        onChange={(next) => onChange('date_of_birth', next)}
        autoComplete="bday"
        max={new Date().toISOString().slice(0, 10)}
      />
      <SexField value={form.sex || ''} onChange={(next) => onChange('sex', next)} />
      <TextField
        label="País emisor del documento"
        value={form.document_issue_country || ''}
        onChange={(next) => onChange('document_issue_country', next.toUpperCase())}
        autoCapitalize="characters"
        maxLength={3}
      />
      <TextField
        label="Vencimiento del documento"
        type="date"
        value={form.document_expiration_date || ''}
        onChange={(next) => onChange('document_expiration_date', next)}
      />
      <TextField
        label="Nacionalidad Migración"
        value={form.migration_nationality || ''}
        onChange={(next) => onChange('migration_nationality', next.toUpperCase())}
        autoCapitalize="characters"
        maxLength={3}
      />
      <TextField
        label="Procedencia"
        value={form.migration_origin || ''}
        onChange={(next) => onChange('migration_origin', next)}
        autoCapitalize="words"
      />
      <TextField
        label="Destino"
        value={form.migration_destination || defaultMigrationDestination}
        onChange={(next) => onChange('migration_destination', next)}
        autoCapitalize="words"
      />
      <TextField
        label="Correo"
        type="email"
        value={form.email}
        onChange={(next) => onChange('email', next)}
        autoComplete="email"
        inputMode="email"
      />
      <label className="block">
        <span className="mb-2 block text-sm font-medium text-wood-dark">Teléfono</span>
        <PhoneInput
          value={form.phone}
          onChange={(next) => onChange('phone', next)}
        />
      </label>
    </div>
  )
}

type TextFieldProps = {
  label: string
  value: string
  onChange: (value: string) => void
  type?: string
  inputMode?: 'text' | 'tel' | 'email' | 'numeric' | 'decimal' | 'search' | 'url' | 'none'
  autoComplete?: string
  autoCapitalize?: 'off' | 'none' | 'on' | 'sentences' | 'words' | 'characters'
  placeholder?: string
  max?: string
  maxLength?: number
}

function TextField({
  label,
  value,
  onChange,
  type = 'text',
  inputMode,
  autoComplete,
  autoCapitalize,
  placeholder,
  max,
  maxLength,
}: TextFieldProps) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-wood-dark">{label}</span>
      <Input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        inputMode={inputMode}
        autoComplete={autoComplete}
        autoCapitalize={autoCapitalize}
        placeholder={placeholder}
        max={max}
        maxLength={maxLength}
      />
    </label>
  )
}

function SexField({
  value,
  onChange,
}: {
  value: MigrationSex
  onChange: (value: MigrationSex) => void
}) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-wood-dark">Sexo</span>
      <select
        value={value || ''}
        onChange={(event) => onChange(event.target.value as MigrationSex)}
        className="flex h-10 w-full rounded-md border border-cream-dark bg-white px-3 py-2 text-sm"
      >
        <option value="">Sin dato</option>
        {Object.entries(sexLabels).map(([key, label]) => (
          <option key={key} value={key}>
            {label}
          </option>
        ))}
      </select>
    </label>
  )
}

function DocumentTypeField({
  value,
  onChange,
}: {
  value: ReservationDocumentType
  onChange: (value: ReservationDocumentType) => void
}) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-wood-dark">Tipo de documento</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value as ReservationDocumentType)}
        className="flex h-10 w-full rounded-md border border-cream-dark bg-white px-3 py-2 text-sm"
      >
        {Object.entries(documentLabels).map(([key, label]) => (
          <option key={key} value={key}>
            {label}
          </option>
        ))}
      </select>
    </label>
  )
}

function PhotoPanel({
  photos,
  busy,
  onUpload,
  onExtract,
}: {
  photos: PublicGuestIntakePhoto[]
  busy: boolean
  onUpload: (file: File) => Promise<void>
  onExtract: (photo: PublicGuestIntakePhoto) => Promise<void>
}) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const handleFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) void onUpload(file)
    event.target.value = ''
  }

  const hasPhoto = photos.length > 0
  return (
    <div className="space-y-3">
      <Button
        type="button"
        variant={hasPhoto ? 'outline' : 'default'}
        className={
          'h-12 w-full ' +
          (hasPhoto
            ? 'border-2 border-terracotta text-terracotta hover:bg-terracotta/10'
            : 'bg-terracotta text-white hover:bg-terracotta/90')
        }
        disabled={busy}
        onClick={() => inputRef.current?.click()}
      >
        <Camera className="mr-2 h-5 w-5" />
        {hasPhoto ? 'Tomar otra foto' : 'Tomar foto del documento'}
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        onChange={handleFile}
        disabled={busy}
        tabIndex={-1}
      />
      {hasPhoto ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {photos.map((photo) => (
            <div key={photo.id} className="rounded-md border border-cream-dark bg-white p-2">
              <img
                src={photo.image_url}
                alt={photo.file_name}
                className="aspect-[4/3] w-full rounded object-cover"
              />
              <div className="mt-2 flex items-center justify-between gap-2">
                <span className="flex min-w-0 items-center gap-1 truncate text-xs text-stone">
                  <Upload className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{photo.file_name}</span>
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => void onExtract(photo)}
                >
                  {busy ? (
                    <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Wand2 className="mr-1 h-3.5 w-3.5" />
                  )}
                  OCR
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}
